import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useSiteContent } from '@/contexts/SiteContentContext';
import EditContentPopup from '@/components/EditContentPopup';

/**
 * Inline-editable button/CTA (admin Edit Mode only): both the label and the link are editable.
 * Usage: <EditableButton id="home.cta.start" defaultLabel="Get Started" defaultUrl="/courses" className="..." />
 * Link rules: starts with http(s) -> opens new tab; otherwise treated as an in-app route.
 */
export default function EditableButton({ id, defaultLabel, defaultUrl = '', className = '', testid }) {
  const { content, editMode, isAdmin, updateContent, resetContent } = useSiteContent();
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();

  const stored = content[id];
  const label = (stored && typeof stored === 'object' && stored.label != null) ? stored.label : defaultLabel;
  const url = (stored && typeof stored === 'object' && stored.url != null) ? stored.url : defaultUrl;

  const go = () => {
    if (!url) return;
    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  if (!isAdmin || !editMode) {
    return (
      <button className={className} onClick={go} data-testid={testid || `btn-${id}`}>
        {label}
      </button>
    );
  }

  return (
    <span className="relative inline-block" style={{ outline: '1px dashed rgba(181,129,59,0.7)', outlineOffset: '3px' }}>
      <button className={className} onClick={(e) => e.preventDefault()} data-testid={testid || `btn-${id}`}>
        {label}
      </button>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(true); }}
        className="absolute -top-3 -right-3 z-40 bg-[#B5813B] text-white rounded-full p-1 shadow-lg hover:bg-[#9c6f31]"
        title="Edit button"
        data-testid={`edit-btn-${id}`}
      >
        <Pencil className="w-3 h-3" />
      </button>
      {editing && (
        <EditContentPopup
          title="Edit button"
          fields={[
            { name: 'label', label: 'Button text', type: 'text', value: label },
            { name: 'url', label: 'Link (in-app path like /courses, or full https:// URL)', type: 'text', value: url, hint: 'Leave blank to keep no link.' },
          ]}
          onSave={async (vals) => { await updateContent(id, { label: vals.label, url: vals.url }); setEditing(false); }}
          onReset={async () => { await resetContent(id); setEditing(false); }}
          onClose={() => setEditing(false)}
        />
      )}
    </span>
  );
}
